import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  getUserId,
  getUserDetails,
  getUserIncomeBreakdown,
  getUserTotalClaimableIncome,
  getUserClaimablePoolIncome,
  getPoolEligibility,
  getTotalTeamSize,
  getCurrentPackage,
  getUserPackages,
  claimAllIncome
} from '../utils/contract';
import { fromWei } from '../utils/web3';
import { sendTelegramNotification } from '../utils/telegram';
import './Home.css';

const PACKAGE_NAMES = ['NeuroCore', 'AlgoCore', 'IntelliCore', 'SynapCore', 'DeepCore', 'DataCore', 'MindCore', 'QuantumCore'];
const PACKAGE_EMOJIS = ['🧠', '💾', '🔷', '⚛️', '🌐', '🤖', '🚀', '⚡'];

/* Animated count-up number (easeOutCubic) */
const CountUp = ({ value, decimals = 2, duration = 1200, prefix = '', suffix = '' }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const end = parseFloat(value) || 0;
    if (end === 0) { setDisplay(0); return; }
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(end * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  const formatted = Number(display).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return <>{prefix}{formatted}{suffix}</>;
};

/* SVG donut chart for income breakdown */
const IncomeDonut = ({ data, total }) => {
  const R = 70;
  const C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="hc-donut-wrap">
      <svg viewBox="0 0 180 180" className="hc-donut">
        <circle cx="90" cy="90" r={R} className="hc-donut-track" />
        {total > 0 && data.map((d, i) => {
          const len = (d.value / total) * C;
          const off = -acc;
          acc += len;
          return (
            <circle
              key={i}
              cx="90" cy="90" r={R}
              className="hc-donut-slice"
              stroke={d.color}
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={off}
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          );
        })}
      </svg>
      <div className="hc-donut-center">
        <span className="hc-donut-total"><CountUp value={total} /></span>
        <span className="hc-donut-label">Total USDT</span>
      </div>
    </div>
  );
};

function Home({ account, onConnect }) {
  const [userDetails, setUserDetails] = useState(null);
  const [incomeBreakdown, setIncomeBreakdown] = useState(null);
  const [claimableIncome, setClaimableIncome] = useState(null);
  const [poolIncomes, setPoolIncomes] = useState({});
  const [poolEligibility, setPoolEligibility] = useState({});
  const [totalTeam, setTotalTeam] = useState(0);
  const [currentPackage, setCurrentPackage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [message, setMessage] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (account) {
      loadUserData();
    }
  }, [account]);

  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => setNotification(null), 5000);
  };

  const copyReferralLink = () => {
    if (userDetails && userDetails.id) {
      const link = `${window.location.origin}/units?ref=${userDetails.id}`;
      navigator.clipboard.writeText(link);
      showNotification('success', '✅ Referral link copied to clipboard!');
    }
  };

  const loadUserData = async () => {
    setLoading(true);
    try {
      const userId = await getUserId(account);

      if (userId && userId !== '0') {
        const [details, breakdown, claimable, packages, team] = await Promise.all([
          getUserDetails(userId),
          getUserIncomeBreakdown(userId),
          getUserTotalClaimableIncome(userId),
          getUserPackages(userId),
          getTotalTeamSize(userId)
        ]);

        const currentPkg = getCurrentPackage(packages);

        setUserDetails(details);
        setIncomeBreakdown(breakdown);
        setClaimableIncome(claimable);
        setCurrentPackage(currentPkg);
        setTotalTeam(team);

        const eligibilityPromises = [];
        for (let pkgId = 2; pkgId <= 8; pkgId++) {
          eligibilityPromises.push(
            getPoolEligibility(userId, pkgId).then(eligible => ({ pkgId, eligible }))
          );
        }
        const eligibilityResults = await Promise.all(eligibilityPromises);

        const eligibilityData = {};
        const eligiblePkgIds = [];
        eligibilityResults.forEach(({ pkgId, eligible }) => {
          eligibilityData[pkgId] = eligible;
          if (eligible) eligiblePkgIds.push(pkgId);
        });

        const poolData = { 2: '0', 3: '0', 4: '0', 5: '0', 6: '0', 7: '0', 8: '0' };
        if (eligiblePkgIds.length > 0) {
          const poolIncomePromises = eligiblePkgIds.map(pkgId =>
            getUserClaimablePoolIncome(userId, pkgId).then(income => ({ pkgId, income }))
          );
          const poolIncomeResults = await Promise.all(poolIncomePromises);
          poolIncomeResults.forEach(({ pkgId, income }) => {
            poolData[pkgId] = income;
          });
        }

        setPoolIncomes(poolData);
        setPoolEligibility(eligibilityData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimAll = async () => {
    setClaiming(true);
    setMessage(null);
    try {
      const claimAmount = claimableIncome ? fromWei(claimableIncome) : '0';
      const tx = await claimAllIncome();
      showNotification('success', '💰 All income claimed successfully!');
      setMessage({ type: 'success', text: '💰 All income claimed successfully!' });

      sendTelegramNotification('claim', {
        amount: claimAmount,
        txHash: tx.hash
      });

      setTimeout(() => {
        loadUserData();
        setMessage(null);
      }, 3000);
    } catch (error) {
      const errorMsg = error.message || 'Failed to claim income';
      showNotification('error', errorMsg);
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setClaiming(false);
    }
  };

  /* ---------- NOT CONNECTED ---------- */
  if (!account) {
    const features = [
      { icon: '💰', title: '30% Direct Rewards', desc: 'Earn instant rewards when your network activates AI systems' },
      { icon: '🎯', title: '2% Level Income', desc: 'Earn from 10 matrix levels deep in the network' },
      { icon: '🌐', title: '2% Generation Income', desc: 'Earn from 10 generation levels in your network' },
      { icon: '👑', title: 'Pool & Royalty', desc: '25% pool allocation + 5% royalty for elite members' },
    ];
    return (
      <div className="container hc">
        <section className="hc-welcome">
          <div className="hc-welcome-orb">
            <img src="/t1.gif" alt="Smartcore" />
          </div>
          <h1 className="hc-welcome-title">Welcome to Smartcore</h1>
          <p className="hc-welcome-sub">Next-Generation AI-Powered Network</p>
          <button className="hc-connect-btn" onClick={onConnect}>
            🔗 Connect Wallet to Access Network
          </button>
        </section>

        <section className="hc-feature-grid">
          {features.map((f) => (
            <div className="hc-feature" key={f.title}>
              <div className="hc-feature-icon">{f.icon}</div>
              <h3 className="hc-feature-title">{f.title}</h3>
              <p className="hc-feature-desc">{f.desc}</p>
            </div>
          ))}
        </section>
      </div>
    );
  }

  /* ---------- LOADING ---------- */
  if (loading) {
    return (
      <div className="container hc">
        <div className="hc-loading">
          <img src="/t1.gif" alt="Loading" />
          <div className="loading"></div>
          <p>Loading your neural network...</p>
        </div>
      </div>
    );
  }

  /* ---------- NOT REGISTERED ---------- */
  if (!userDetails || !incomeBreakdown) {
    return (
      <div className="container hc">
        <div className="hc-register">
          <div className="hc-register-icon">🤖</div>
          <h2>Account Not Registered</h2>
          <p>Initialize your neural core to get started</p>
          <Link to="/units" className="hc-connect-btn">🚀 Initialize Neural Core</Link>
        </div>
      </div>
    );
  }

  /* ---------- MAIN DASHBOARD ---------- */
  const claimable = claimableIncome ? parseFloat(fromWei(claimableIncome.totalClaimable)) : 0;
  const poolPart = claimableIncome ? fromWei(claimableIncome.poolIncome) : '0';
  const royaltyPart = claimableIncome ? fromWei(claimableIncome.royaltyIncome) : '0';

  const incomeData = [
    { label: 'Direct', value: parseFloat(fromWei(incomeBreakdown.directReferralIncome)), color: '#34f5c5', icon: '💰' },
    { label: 'Matrix', value: parseFloat(fromWei(incomeBreakdown.matrixLevelIncome)), color: '#22d3ee', icon: '🎯' },
    { label: 'Generation', value: parseFloat(fromWei(incomeBreakdown.normalLevelIncome)), color: '#a855f7', icon: '🌐' },
    { label: 'Royalty', value: parseFloat(fromWei(incomeBreakdown.royaltyIncome)), color: '#fbbf24', icon: '👑' },
  ];
  const totalIncomeSum = incomeData.reduce((s, d) => s + d.value, 0);

  const metrics = [
    { label: 'Total Income', value: parseFloat(fromWei(incomeBreakdown.totalIncome)), decimals: 2, sub: 'USDT', accent: '#22d3ee' },
    { label: 'Network Size', value: totalTeam, decimals: 0, sub: 'Active Cores', accent: '#a855f7' },
    { label: 'Direct Cores', value: parseInt(userDetails.totalDirectReferrals) || 0, decimals: 0, sub: 'Referrals', accent: '#ec4899' },
    { label: 'Available', value: claimable, decimals: 2, sub: 'USDT', accent: '#34f5c5' },
  ];

  return (
    <div className="container hc">
      {notification && createPortal(
        <div className={`hc-toast ${notification.type}`}>
          <span className="hc-toast-icon">
            {notification.type === 'success' ? '✓' : notification.type === 'error' ? '!' : 'i'}
          </span>
          <span className="hc-toast-text">{notification.text}</span>
        </div>,
        document.body
      )}

      {/* ===== COMMAND BAR ===== */}
      <header className="hc-topbar">
        <div className="hc-greet">
          <span className="hc-greet-eyebrow">Neural Core Dashboard</span>
          <h1 className="hc-greet-title">
            Welcome back, <span>Core #{userDetails.id}</span>
          </h1>
        </div>
        <div className="hc-topbar-right">
          <div className="hc-chip">
            <span className="hc-chip-label">Upline</span>
            <span className="hc-chip-value">{userDetails.referrer || 'None'}</span>
          </div>
          <div className="hc-pkg-badge">
            <span className="hc-pkg-emoji">
              {currentPackage > 0 ? PACKAGE_EMOJIS[currentPackage - 1] : '🤖'}
            </span>
            <div>
              <span className="hc-pkg-label">Active System</span>
              <span className="hc-pkg-name">
                {currentPackage > 0 ? PACKAGE_NAMES[currentPackage - 1] : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* ===== HERO BALANCE CENTERPIECE ===== */}
      <section className="hc-hero">
        <div className="hc-hero-rings"><span /><span /><span /></div>
        <div className="hc-hero-inner">
          <div className="hc-hero-left">
            <span className="hc-hero-label">Total Claimable Income</span>
            <div className="hc-hero-amount">
              <CountUp value={claimable} /> <span className="hc-hero-unit">USDT</span>
            </div>
            <div className="hc-hero-breakdown">
              <span>👑 Royalty: <b>{royaltyPart}</b></span>
            </div>
          </div>
          <div className="hc-hero-action">
            {claimable > 0 ? (
              <button className="hc-claim-btn" onClick={handleClaimAll} disabled={claiming}>
                {claiming ? (
                  <><span className="hc-spin" /> Processing...</>
                ) : (
                  <>💰 Claim All Income</>
                )}
              </button>
            ) : (
              <button className="hc-claim-btn disabled" disabled>💤 Nothing to Claim</button>
            )}
          </div>
        </div>
        {message && (
          <div className={`alert alert-${message.type}`} style={{ marginTop: '18px' }}>
            {message.text}
          </div>
        )}
      </section>

      {/* ===== METRIC STRIP ===== */}
      <section className="hc-metrics">
        {metrics.map((m) => (
          <div className="hc-metric" key={m.label} style={{ '--accent': m.accent }}>
            <span className="hc-metric-label">{m.label}</span>
            <span className="hc-metric-value">
              <CountUp value={m.value} decimals={m.decimals} />
            </span>
            <span className="hc-metric-sub">{m.sub}</span>
          </div>
        ))}
      </section>

      {/* ===== INCOME CHART + REFERRAL ===== */}
      <section className="hc-cols">
        <div className="hc-panel hc-income-panel">
          <h3 className="hc-panel-title">📊 Income Breakdown</h3>
          <div className="hc-income-body">
            <IncomeDonut data={incomeData} total={totalIncomeSum} />
            <ul className="hc-legend">
              {incomeData.map((d) => (
                <li key={d.label}>
                  <span className="hc-legend-dot" style={{ background: d.color }} />
                  <span className="hc-legend-name">{d.icon} {d.label}</span>
                  <span className="hc-legend-val">{d.value.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="hc-panel hc-ref-panel">
          <h3 className="hc-panel-title">🔗 Your Network Link</h3>
          <p className="hc-ref-hint">Share your link and grow your neural network.</p>
          <div className="hc-ref-link">
            {window.location.origin}/units?ref={userDetails.id}
          </div>
          <button className="hc-ref-copy" onClick={copyReferralLink}>📋 Copy Referral Link</button>
          <div className="hc-ref-stats">
            <div><b>{userDetails.totalDirectReferrals || 0}</b><span>Direct</span></div>
            <div><b>{totalTeam}</b><span>Network</span></div>
          </div>
        </div>
      </section>

      {/* ===== QUICK ACTIONS ===== */}
      <section className="hc-actions">
        <Link to="/units" className="hc-action">
          <span className="hc-action-icon">🤖</span>
          <span className="hc-action-title">AI Cores</span>
        </Link>
        <Link to="/team" className="hc-action">
          <span className="hc-action-icon">👥</span>
          <span className="hc-action-title">Network</span>
        </Link>
        <Link to="/record" className="hc-action">
          <span className="hc-action-icon">📊</span>
          <span className="hc-action-title">Analytics</span>
        </Link>
      </section>
    </div>
  );
}

export default Home;
